import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import useUser from "@/hooks/useUser"
import { useQuery } from "@tanstack/react-query"
import { lazy, Suspense, useEffect, useState } from "react"
import { catalog } from "./api"

const PdfHighlightViewer = lazy(() =>
  import(
    "@/components/feature/Modals/FilePreviewModal/PdfHighlightViewer"
  ).then(({ PdfHighlightViewer }) => ({ default: PdfHighlightViewer })),
)

export function DocumentPreview({
  id,
  page = 1,
  quote,
}: {
  id: string
  page?: number
  quote?: string
}) {
  const { user } = useUser()
  const { translate: t } = useTranslation()
  const preview = useQuery({
    queryKey: ["company-index-document-preview", user?.organization, id],
    queryFn: () => catalog.preview(id),
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  })
  const [resource, setResource] = useState<{ blob: Blob; url: string } | null>(
    null,
  )
  useEffect(() => {
    if (!preview.data) return
    const url = URL.createObjectURL(preview.data)
    // Publish the external Blob URL after commit; cleanup must revoke the exact URL.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResource({ blob: preview.data, url })
    return () => URL.revokeObjectURL(url)
  }, [preview.data])
  if (preview.isError)
    return (
      <div role="alert">
        {t("CI_PREVIEW_ERROR")}{" "}
        <Button variant="outline" onClick={() => void preview.refetch()}>
          {t("CI_RETRY")}
        </Button>
      </div>
    )
  if (!resource || resource.blob !== preview.data)
    return <p>{t("CI_LOADING")}</p>
  return (
    <div className="h-[min(70vh,800px)] min-h-[480px] overflow-hidden rounded-md border bg-background">
      <Suspense fallback={<p>{t("CI_LOADING")}</p>}>
        <PdfHighlightViewer
          key={`${page}:${quote ?? ""}`}
          fileUrl={resource.url}
          initialPage={page}
          searchText={quote}
        />
      </Suspense>
    </div>
  )
}
